import { SizeCode } from 'entities/SizeCode';
import { isBoxShape, Shape } from '../../model/types';
import { getNearestSizeCode } from './getNearestSizeCode';
import { getShapeSquareMeters } from './shapeGeometry';

/**
 * Single source of truth for "auto-pick the nearest size code for this shape".
 * Used by every shape-creation path (rectangle, circle, polygon-box) and by
 * the `Reset to nearest` action in `ParametersBar`. Non-box shapes (line,
 * pencil, text, free polygon) are returned unchanged — `sizeCodeId` lives
 * on `WithSizeCode` and would be a noise field on those types.
 */
export const assignNearestSizeCode = (shape: Shape, availableSizeCodes: SizeCode[] | undefined, pixelsPerMeter: number): Shape => {
  if (!isBoxShape(shape)) return shape;
  if (!availableSizeCodes?.length) return shape;

  const squareMeters = getShapeSquareMeters(shape, pixelsPerMeter);
  if (squareMeters <= 0) return shape;

  // `getNearestSizeCode` re-derives the square from width/length, so we feed
  // it a square root in both axes — the helper only cares about their product.
  const sidePx = Math.sqrt(squareMeters) * pixelsPerMeter;

  const nearest = getNearestSizeCode({
    shapeWidth: sidePx,
    shapeLength: sidePx,
    availableSizeCodes,
    pixelsPerMeter,
  });

  if (!nearest) return shape;

  return { ...shape, sizeCodeId: nearest.sizeCodeId };
};
